<template>
	<div class="points-claim-wrapper">
		<v-btn v-if="!readonly || claimed" class="points-claim-btn" @click="openDialog" :disabled="claimed || points <= 0">
			<v-icon v-if="claimed">check</v-icon>
			<v-icon v-else>paid</v-icon>
			<span class="points-claim-btn__text" v-if="claimed">{{ t('PointsRequestSent') }}: {{ formatPoints(claimedPoints) }} {{ t('pts') }}</span>
			<span class="points-claim-btn__text" v-else>{{ t('Claim') }}</span>
		</v-btn>

		<PointsClaimDialog v-model="dialogOpen" :promo-name="promoName" :calculated-points="points" :loading="loading" :error="claimError" :overlay-target="overlayTarget" @confirm="handleConfirm" />
	</div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import PointsClaimDialog from './PointsClaimDialog.vue';
import { getPointsHistory, matchesOfferContext, isRealUid, getPendingClaim, savePendingClaim, removePendingClaim, markPendingClaimValidated, submitPendingClaim, formatPoints } from '@/api/sales/points';
import { eventBus } from '@/utils/core/eventBus';
import { useTranslator } from '@/composables/useTranslator';
import { log } from '@/utils/core/vueLogger';

const t = useTranslator();

const props = defineProps({
	campaignId: { type: [String, Number], default: null },
	offerId: { type: [String, Number], default: null },
	appointmentUid: { type: String, default: null },
	deliveryLocationId: { type: [String, Number], default: null },
	promoName: { type: String, default: '' },
	points: { type: Number, default: 0 },
	overlayTarget: { type: Object, default: null },
	campaignOfferStartDate: { type: String, default: null },
	campaignOfferEndDate: { type: String, default: null },
	readonly: { type: Boolean, default: false },
});

const dialogOpen = ref(false);
const loading = ref(false);
const claimed = ref(false);
const claimedPoints = ref(0);
const claimError = ref(null);

function offerContext() {
	return {
		campaignId: props.campaignId,
		offerId: props.offerId,
		campaignOfferStartDate: props.campaignOfferStartDate,
		campaignOfferEndDate: props.campaignOfferEndDate,
		deliveryLocationId: props.deliveryLocationId,
	};
}

function openDialog() {
	if (props.readonly && !claimed.value) return;
	claimError.value = null;
	dialogOpen.value = true;
}

// Re-claim guard: if a PENDING claim already exists for this store+campaign+offer+period,
// reflect it instead of allowing a 2nd claim. Also restore any locally-stored pending intent.
onMounted(async () => {
	// Restore pending intent from localStorage (claim stored but not yet submitted)
	const pending = getPendingClaim(offerContext());
	if (pending) {
		claimed.value = true;
		claimedPoints.value = pending.pointsClaimed;
	}

	let historyChecked = false;
	let foundOnServer = false;
	try {
		const history = await getPointsHistory();
		historyChecked = true;
		const existing = history?.find((e) => e.status === 'PENDING' && matchesOfferContext(e, offerContext()));
		if (existing) {
			foundOnServer = true;
			claimed.value = true;
			claimedPoints.value = existing.pointsClaimed;
			// If the claim is already on the server, clear any stale localStorage entry
			removePendingClaim(offerContext());
		}
	} catch {
		// non-blocking guard — ignore failures
	}

	// A local pending claim is only submitted once the backing visit is validated (see
	// handleAppointmentValidated below). If that already happened in a previous session (this
	// mount is a fresh page load/reopen) but the server has no matching record, the validation
	// event won't fire again — retry the submission now instead of leaving the button stuck
	// forever showing a claim that was never actually sent. `validated` is what tells the two
	// apart: without it this retry also fires for a visit still in progress, on every Knockout
	// re-render of the promo panel.
	if (pending?.validated && historyChecked && !foundOnServer) {
		await submitAndReflect(pending, isRealUid(props.appointmentUid) ? props.appointmentUid : null);
	}

	// Visit validation is signalled through the legacy amplify 'AppointmentUpdated' event,
	// relayed onto the Vue eventBus by iframeWatcher. We can't use a window CustomEvent here:
	// the legacy view runs in the iframe window while this component runs in the parent window.
	// NB: this only gives immediate UI feedback while this zone happens to still be mounted when
	// the event fires — PointsDialogZone (persistent) is the actual safety net, since closing the
	// visit can unmount this zone before the deferred event arrives (RG #22908).
	eventBus.on('appointments-updated', handleAppointmentValidated);
});

onBeforeUnmount(() => {
	eventBus.off('appointments-updated', handleAppointmentValidated);
});

async function handleAppointmentValidated(data) {
	const pending = getPendingClaim(offerContext());
	if (!pending) return;

	// Only handle if this zone belongs to the validated visit's delivery location (when known)
	const evtDeliveryLocationId = data?.deliveryLocationId;
	if (evtDeliveryLocationId != null && String(evtDeliveryLocationId) !== String(props.deliveryLocationId)) {
		log('warn', 'Points', `⚠️ appointments-updated ignored: event deliveryLocationId=${evtDeliveryLocationId} vs zone deliveryLocationId=${props.deliveryLocationId}`);
		return;
	}

	markPendingClaimValidated(offerContext());

	// Resolve the freshest appointment uid: prefer the just-validated visit's uid (post-synch
	// server GUID), fall back to props. Null when still a temp/empty uid.
	const uid = isRealUid(data?.appointmentUid) ? data.appointmentUid : isRealUid(props.appointmentUid) ? props.appointmentUid : null;

	log('info', 'Points', `📨 appointments-updated received for pending claim ${pending.pointsClaimed}pts, resolved uid=${uid}`);
	await submitAndReflect(pending, uid);
}

// Delegates the actual submit/queue to the shared points API (guarded there against double-submit
// with PointsDialogZone's reconciliation pass) and reflects the outcome in this button's own UI.
async function submitAndReflect(pending, uid) {
	loading.value = true;
	claimError.value = null;
	try {
		await submitPendingClaim(pending, uid);
		claimedPoints.value = pending.pointsClaimed;
		claimed.value = true;
	} catch (error) {
		claimError.value = error.message === 'NO_ACTIVE_PERIOD' ? t('NoActiveScoringPeriod') : t('ClaimError');
		log('warn', 'Points', `⚠️ Deferred claim submission error: ${error.message}`);
	} finally {
		loading.value = false;
	}
}

function handleConfirm({ pointsClaimed, comment }) {
	// Store the intent locally — actual API call is deferred to visit validation. appointmentUid
	// and promoName are kept alongside so PointsDialogZone can reconcile this claim later even if
	// this component/zone gets unmounted first.
	savePendingClaim(offerContext(), {
		campaignId: props.campaignId,
		offerId: props.offerId,
		deliveryLocationId: props.deliveryLocationId,
		pointsClaimed,
		comment,
		campaignOfferStartDate: props.campaignOfferStartDate,
		campaignOfferEndDate: props.campaignOfferEndDate,
		appointmentUid: props.appointmentUid,
		promoName: props.promoName,
	});

	dialogOpen.value = false;
	claimedPoints.value = pointsClaimed;
	claimed.value = true;
	log('info', 'Points', `🎯[CLAIM-DEBUG] intent stored: ${pointsClaimed}pts dlId=${props.deliveryLocationId} campaignId=${props.campaignId} offerId=${props.offerId} apptUid=${props.appointmentUid ?? 'none'}`);
}
</script>
